import { IconImageInPicture } from '@tabler/icons-react'
import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import styles from '../../styles/admin/StudyContent.module.scss'

const ImageDropzone = ({
  imageFile,
  onImageChange,
  existingImageUrl,
  iconSize = 48,
  className,
  placeholderText = 'Drag & Drop or Click to Upload (350x250)',
  label = 'Image',
  showLabel = false,
  onExistingImageRemove,
  targetWidth = 350,
  targetHeight = 250,
  quality = 0.9,
}) => {
  const resizeImage = useCallback((file, width, height, quality) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()

      img.onload = () => {
        canvas.width = width
        canvas.height = height

        const sourceRatio = img.width / img.height
        const targetRatio = width / height

        let sx = 0,
          sy = 0,
          sw = img.width,
          sh = img.height

        if (sourceRatio > targetRatio) {
          sw = img.height * targetRatio
          sx = (img.width - sw) / 2
          sh = img.width / targetRatio
          sy = (img.height - sh) / 2
        }

        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height)

        canvas.toBlob(
          (blob) => {
            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            })
            resolve(resizedFile)
          },
          file.type,
          quality
        )
      }

      img.src = URL.createObjectURL(file)
    })
  }, [])

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0]
        if (file.type.startsWith('image/')) {
          try {
            const resizedFile = await resizeImage(
              file,
              targetWidth,
              targetHeight,
              quality
            )
            onImageChange(resizedFile)
          } catch (error) {
            console.error('이미지 리사이즈 실패:', error)
            onImageChange(file)
          }
        }
      }
    },
    [onImageChange, resizeImage, targetWidth, targetHeight, quality]
  )

  const handleRemoveImage = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()

      console.log('🔥 삭제 클릭!', { imageFile, existingImageUrl })

      if (imageFile) {
        console.log('✅ onImageChange(null) 호출')
        onImageChange(null)
      } else if (existingImageUrl && onExistingImageRemove) {
        console.log('✅ onExistingImageRemove() 호출')
        onExistingImageRemove()
      }
    },
    [imageFile, existingImageUrl, onImageChange, onExistingImageRemove]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
    },
    multiple: false,
    maxSize: 5 * 1024 * 1024,
  })

  const hasImage = imageFile || existingImageUrl

  return (
    <div className={styles.imageDropzoneContainer}>
      <div className={styles.imageDropzoneWrapper}>
        {showLabel && (
          <span
            style={{
              fontSize: '15px',
              fontWeight: 700,
              color: '#111',
            }}
          >
            {label}
          </span>
        )}
        <div style={{ marginBottom: '12px' }}></div>

        <div style={{ position: 'relative' }}>
          {hasImage && (
            <button
              type="button"
              onClick={handleRemoveImage}
              className={styles.removeButton}
            >
              ×
            </button>
          )}

          <div
            {...getRootProps()}
            className={`${className || styles.imageDropzone} ${
              isDragActive ? styles.dragActive : ''
            }`}
          >
            <input {...getInputProps()} />

            {imageFile ? (
              <img
                src={URL.createObjectURL(imageFile)}
                alt="Preview"
                className={styles.previewImage}
              />
            ) : existingImageUrl ? (
              <img
                src={existingImageUrl}
                alt="Existing"
                className={styles.existingImage}
              />
            ) : (
              <div className={styles.uploadPlaceholder}>
                <IconImageInPicture size={iconSize} />
                <p>
                  {isDragActive ? 'Drop the files here...' : placeholderText}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImageDropzone
